(function () {
  'use strict';

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-drawer-overlay]');
      if (!this.drawer) return;

      this.trashIconHtml = document.getElementById('CartDrawerIconTrash')?.innerHTML.trim() || '';

      this.init();
    }

    init() {
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.drawer.querySelector('[data-cart-drawer-close]')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('cart:updated', () => this.refresh());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
      });

      this.drawer.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-cart-qty-minus]');
        const plus = e.target.closest('[data-cart-qty-plus]');
        const remove = e.target.closest('[data-cart-remove]');

        if (minus || plus) {
          const key = (minus || plus).dataset.key;
          const input = this.drawer.querySelector(`[data-cart-qty-input="${key}"]`);
          let qty = parseInt(input.value, 10) || 1;
          qty = minus ? Math.max(0, qty - 1) : qty + 1;
          this.updateItem(key, qty);
        }

        if (remove) {
          this.updateItem(remove.dataset.key, 0);
        }
      });

      window.LuxeraCartDrawer = this;
    }

    open() {
      this.refresh();
      this.drawer.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.drawer.setAttribute('aria-hidden', 'false');
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      document.body.style.overflow = '';
      this.drawer.setAttribute('aria-hidden', 'true');
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
        this.updateCount(cart.item_count);
      } catch (err) {
        console.error(err);
      }
    }

    async updateItem(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();
        this.render(cart);
        this.updateCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      } catch (err) {
        console.error(err);
      }
    }

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? 'inline-flex' : 'none';
      });
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    renderItemImage(item) {
      if (item.image) {
        return `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy" width="80" height="80">`;
      }

      return '<div class="cart-drawer__item-image cart-drawer__item-image--placeholder" aria-hidden="true"></div>';
    }

    renderItemVariant(item) {
      const variantTitle = item.variant_title;
      if (!variantTitle || variantTitle === 'Default Title') return '';

      return `<p class="cart-drawer__item-variant">${this.escapeHtml(variantTitle)}</p>`;
    }

    render(cart) {
      const itemsContainer = this.drawer.querySelector('[data-cart-items]');
      const emptyEl = this.drawer.querySelector('[data-cart-empty]');
      const subtotalEl = this.drawer.querySelector('[data-cart-subtotal]');
      const totalEl = this.drawer.querySelector('[data-cart-total]');
      const titleEl = this.drawer.querySelector('[data-cart-title]');
      const progressBar = this.drawer.querySelector('[data-shipping-progress]');
      const progressText = this.drawer.querySelector('[data-shipping-text]');
      const footerEl = this.drawer.querySelector('[data-cart-footer]');
      const upsellEl = this.drawer.querySelector('[data-cart-upsell]');

      const titleTemplate = this.drawer.dataset.cartTitleTemplate || 'Your Cart (__COUNT__)';
      if (titleEl) {
        titleEl.textContent = titleTemplate.replace('__COUNT__', cart.item_count);
      }

      const threshold = window.themeSettings?.freeShippingThreshold || 10000;
      const remaining = Math.max(0, threshold - cart.total_price);
      const progress = Math.min(100, (cart.total_price / threshold) * 100);

      if (progressBar) progressBar.style.width = `${progress}%`;
      if (progressText) {
        if (remaining <= 0) {
          progressText.textContent = 'Congrats! You are eligible for FREE SHIPPING.';
        } else {
          progressText.textContent = `You're ${this.formatMoney(remaining)} away from FREE SHIPPING!`;
        }
      }

      if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);
      if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price);

      const isEmpty = !cart.items || cart.items.length === 0;
      this.drawer.classList.toggle('cart-drawer--empty', isEmpty);
      if (footerEl) footerEl.hidden = isEmpty;
      if (upsellEl) upsellEl.hidden = isEmpty;
      if (emptyEl) emptyEl.hidden = !isEmpty;

      if (!itemsContainer) return;

      if (isEmpty) {
        itemsContainer.innerHTML = '';
        return;
      }

      itemsContainer.innerHTML = cart.items.map((item) => `
        <div class="cart-drawer__item" data-cart-item="${this.escapeHtml(item.key)}">
          ${this.renderItemImage(item)}
          <div class="cart-drawer__item-details">
            <h4 class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</h4>
            ${this.renderItemVariant(item)}
            <p class="cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</p>
            <div class="cart-drawer__item-actions">
              <div class="quantity-selector">
                <button type="button" class="quantity-selector__btn" data-cart-qty-minus data-key="${this.escapeHtml(item.key)}" aria-label="Decrease">−</button>
                <input type="number" class="quantity-selector__input" value="${item.quantity}" data-cart-qty-input="${this.escapeHtml(item.key)}" readonly>
                <button type="button" class="quantity-selector__btn" data-cart-qty-plus data-key="${this.escapeHtml(item.key)}" aria-label="Increase">+</button>
              </div>
              <div class="cart-drawer__item-icons">
                <button type="button" class="cart-drawer__item-icon cart-drawer__remove" data-cart-remove data-key="${this.escapeHtml(item.key)}" aria-label="Remove item">
                  ${this.trashIconHtml}
                </button>
              </div>
            </div>
          </div>
        </div>
      `).join('');
    }

    formatMoney(cents) {
      if (typeof window.themeFormatMoney === 'function') {
        return window.themeFormatMoney(cents);
      }

      const moneyFormat = window.themeSettings?.moneyFormat;
      if (moneyFormat && typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, moneyFormat);
      }

      return (cents / 100).toFixed(2);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawer();
  });
})();
